import React, { useState, useEffect } from 'react';
import { Play, ChevronLeft, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Extract YouTube video ID from various URL formats
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/shorts\/([^&\n?#]+)/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

const VideoSection = ({ t }) => {
  const [videos, setVideos] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await fetch(`${API_URL}/api/videos`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            setVideos(data);
          }
        }
      } catch (error) {
        console.log('No videos available');
      }
    };

    fetchVideos();
  }, []);

  const handleNext = () => {
    setIsPlaying(false);
    setActiveIndex((prev) => (prev + 1) % videos.length);
  };

  const handlePrev = () => {
    setIsPlaying(false);
    setActiveIndex((prev) => (prev - 1 + videos.length) % videos.length);
  };

  // If no videos, show placeholder
  if (videos.length === 0) {
    return (
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold text-[#0d2e5d] mb-4">
              {t.video.title}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t.video.subtitle}
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl group cursor-pointer bg-gray-200">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <Play className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 font-medium">
                    {t.nav.home === 'Acasă' ? 'Videoclipuri în curând' : 'Videos coming soon'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const currentVideo = videos[activeIndex];
  const videoId = getYouTubeVideoId(currentVideo?.youtube_url);

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold text-[#0d2e5d] mb-4">
            {t.video.title}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t.video.subtitle}
          </p>
        </div>

        {/* Video Player */}
        <div className="max-w-5xl mx-auto">
          <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl">
            {isPlaying && videoId ? (
              <iframe
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
                title={currentVideo.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <>
                {/* Thumbnail */}
                <img
                  src={videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : 'https://images.unsplash.com/photo-1686178827149-6d55c72d81df'}
                  alt={currentVideo?.title || 'Video Thumbnail'}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback to medium quality if maxres doesn't exist
                    e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                  }}
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/40"></div>
                
                {/* Play Button */}
                <button
                  onClick={() => setIsPlaying(true)}
                  className="absolute inset-0 flex items-center justify-center group"
                >
                  <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-xl">
                    <Play className="w-8 h-8 text-[#2196f3] ml-1" fill="currentColor" />
                  </div>
                </button>
              </>
            )}
          </div>

          {/* Navigation (if multiple videos) */}
          {videos.length > 1 && (
            <div className="flex items-center justify-center gap-4 mt-6">
              <button
                onClick={handlePrev}
                className="w-10 h-10 rounded-full border-2 border-[#2196f3] flex items-center justify-center hover:bg-[#2196f3] hover:text-white transition-colors text-[#2196f3]"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              
              <div className="flex gap-2">
                {videos.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setIsPlaying(false);
                      setActiveIndex(index);
                    }}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === activeIndex
                        ? 'bg-[#2196f3] w-8'
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
              
              <button
                onClick={handleNext}
                className="w-10 h-10 rounded-full border-2 border-[#2196f3] flex items-center justify-center hover:bg-[#2196f3] hover:text-white transition-colors text-[#2196f3]"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Video Info */}
          <div className="mt-6 text-center">
            <h3 className="text-xl font-bold text-[#0d2e5d]">{currentVideo?.title}</h3>
            {currentVideo?.description && (
              <p className="text-gray-600 mt-2 max-w-3xl mx-auto">
                {currentVideo.description}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default VideoSection;
